import React from 'react';
import { type MainMapView } from './mapUtils';

/**
 * Rail latéral — habillage repris du prototype (`.rail`) : 66 px, icône + micro-libellé
 * empilés, deux familles OPS / Gestion, palette très sombre.
 */

export const RAIL_WIDTH = 66;

/** Icônes SVG reprises du prototype (viewBox 0 0 24 24, remplissage courant). */
const ICON: Record<string, string> = {
  Dispatch: 'M12 2 4 6v6c0 5 3.4 9.4 8 10 4.6-.6 8-5 8-10V6l-8-4Z',
  'Synoptique moyens': 'M3 13h11l2-5h4l1 5v5h-2a2 2 0 1 1-4 0H9a2 2 0 1 1-4 0H3v-5Z',
  'Synoptique interventions': 'M12 2 1 21h22L12 2Zm0 6 6 11H6l6-11Zm-1 4h2v3h-2v-3Zm0 4h2v2h-2v-2Z',
  Historique: 'M5 3h11l3 3v15H5V3Zm3 6h8v1.6H8V9Zm0 4h8v1.6H8V13Zm0 4h5v1.6H8V17Z',
  'Réseau': 'M3 4h7v7H3V4Zm11 0h7v4h-7V4ZM3 13h7v7H3v-7Zm11-2h7v9h-7v-9Z',
  Ressources: 'M3 3h2v18H3V3Zm4 10h3v8H7v-8Zm5-6h3v14h-3V7Zm5 3h3v11h-3V10Z',
  'Bâtiments': 'M4 21V8l6-4 6 4v13h-5v-5H9v5H4Zm14 0V10l2 1.4V21h-2Z',
  Technologies: 'M11 2h2v5h-2V2Zm-7 7h5v2H4V9Zm11 0h5v2h-5V9Zm-4 4h2v9h-2v-9Zm-4-4h10v2H7V9Z',
  Alliances: 'M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm-8 9a8 8 0 0 1 16 0H4Z',
  Formations: 'M12 3 1 8l11 5 9-4.1V16h2V8L12 3ZM5 12.2V16c0 1.7 3.1 3 7 3s7-1.3 7-3v-3.8l-7 3.2-7-3.2Z',
  'Météo': 'M6 18a4 4 0 0 1 0-8 5 5 0 0 1 9.6-1.4A3.5 3.5 0 0 1 18 18H6Z',
};

const OPS_ITEMS = ['Dispatch', 'Synoptique moyens', 'Synoptique interventions', 'Historique'] as const;
const GESTION_ITEMS = ['Réseau', 'Ressources', 'Bâtiments', 'Technologies'] as const;
const FUTURE_ITEMS_LIST = ['Alliances', 'Formations', 'Météo'] as const;
const FUTURE_ITEMS = new Set<string>(FUTURE_ITEMS_LIST);

/** Libellé court affiché sous l'icône (le rail est étroit). */
const SHORT: Record<string, string> = {
  'Synoptique moyens': 'Moyens',
  'Synoptique interventions': 'Interv.',
  Technologies: 'Technos',
  Ressources: 'Ressources',
};

const VIEW_MAP: Record<string, MainMapView> = {
  Dispatch: 'dispatch',
  'Synoptique moyens': 'moyens',
  'Synoptique interventions': 'interventions',
  Historique: 'historique',
  'Réseau': 'reseau',
  Ressources: 'resources',
  'Bâtiments': 'buildings',
  Technologies: 'tech-tree',
};

interface MapRailProps {
  activeMainView: MainMapView;
  setMainView: (view: MainMapView) => void;
  handleFutureFeature: (label: string) => void;
  topOffset: number;
}

const GROUPS: ReadonlyArray<{ key: string; label: string; items: readonly string[]; mgmt: boolean }> = [
  { key: 'ops', label: 'Ops', items: OPS_ITEMS, mgmt: false },
  { key: 'gestion', label: 'Gestion', items: GESTION_ITEMS, mgmt: true },
  { key: 'future', label: 'À venir', items: FUTURE_ITEMS_LIST, mgmt: true },
];

const MapRail: React.FC<MapRailProps> = ({ activeMainView, setMainView, handleFutureFeature, topOffset }) => (
  <nav
    aria-label="Navigation du cockpit"
    className="absolute bottom-0 left-0 z-[1180] flex flex-col gap-px overflow-y-auto"
    style={{
      top: `${topOffset}px`,
      width: `${RAIL_WIDTH}px`,
      padding: '6px 4px',
      background: 'linear-gradient(180deg,#0c1421,#080d16)',
      borderRight: '1px solid #223046',
    }}
  >
    {GROUPS.map((group) => (
      <React.Fragment key={group.key}>
        <p
          style={{
            fontSize: 7.5,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: '#4d5f78',
            padding: '5px 4px 3px',
            textAlign: 'center',
          }}
        >
          {group.label}
        </p>
        {group.items.map((item) => {
          const isFuture = FUTURE_ITEMS.has(item);
          const targetView = VIEW_MAP[item];
          const isActive = targetView != null && activeMainView === targetView;
          return (
            <button
              key={item}
              type="button"
              id={targetView ? `nav-${targetView}` : undefined}
              onClick={() => {
                if (targetView) {
                  setMainView(targetView);
                  return;
                }
                handleFutureFeature(item);
              }}
              title={isFuture ? `${item} — bientôt` : item}
              className="rail-item"
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                padding: '7px 2px 6px',
                borderRadius: 5,
                color: isActive ? '#6ee7b7' : isFuture ? '#3f4f66' : '#7f92ab',
                background: isActive ? 'rgba(16,185,129,.12)' : 'transparent',
                transition: '.14s',
                // survol géré en JS pour respecter la teinte bleue « gestion » du proto
              }}
              onMouseEnter={(e) => {
                if (isActive) return;
                e.currentTarget.style.background = group.mgmt ? 'rgba(56,189,248,.10)' : '#131e2f';
                e.currentTarget.style.color = group.mgmt ? '#7dd3fc' : '#dce5f2';
              }}
              onMouseLeave={(e) => {
                if (isActive) return;
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = isFuture ? '#3f4f66' : '#7f92ab';
              }}
            >
              <svg viewBox="0 0 24 24" style={{ width: 17, height: 17, fill: 'currentColor' }}>
                <path d={ICON[item] || 'M4 4h16v16H4z'} />
              </svg>
              <span style={{ fontSize: 8.5, letterSpacing: '0.03em', textAlign: 'center', lineHeight: 1.1 }}>
                {SHORT[item] || item}
              </span>
            </button>
          );
        })}
      </React.Fragment>
    ))}
  </nav>
);

export default MapRail;
